import { Can } from '@/components/can/Can';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useHostList } from '@/features/store-management/premium-stores/hooks/useGetHostList';
import { HostManagementTable } from '@/features/store-management/premium-stores/hostManagement/table';
import { Translation } from '@/hoslog-intl';
import { PlusIcon } from '@/icons/plus-icon';
import { createFileRoute, Link, SearchSchemaInput } from '@tanstack/react-router';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum(['position', 'hostNameEng', 'rank', 'status', 'createdDate', 'updatedDate']),
  desc: z.boolean(),
});
const HostManagementSearchSchema = z.object({
  breadcrumbReturnTo: z.string().optional(),

  sort: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return val;
    }, z.array(sortItemSchema))
    .optional(),

  createdDate: z
    .string()

    .optional(),

  q: z
    .any()
    .transform((val) => (val == null ? undefined : String(val)))
    .optional(),

  status: z
    .string()
    .transform((val) => val.split(','))
    .pipe(z.array(z.enum(['Active', 'Blocked'])))
    .optional(),

  page: z.coerce.number().positive().catch(0),
  fetch: z.coerce.number().positive().max(100).catch(10),
});
export const Route = createFileRoute('/(app)/store-information/premium-stores/host-management/$clubid/')({
  component: RouteComponent,
  loader: ({ params }) => {
    const renderActions = (
      <Can rule={{ allOf: [PERMS.host.premium.create] }}>
        <div>
          <Link
            to="/store-information/premium-stores/host-management/$clubid/add-host"
            params={{
              clubid: `${params['clubid']}`,
            }}
            search={(search) => search}
          >
            <Button type="submit" className="w-[110px] text-copy font-bold">
              <PlusIcon /> <Translation id="common.page.button.addNew" />
            </Button>
          </Link>
        </div>
      </Can>
    );
    return {
      actions: { renderActions },
    };
  },
  validateSearch: (search: SearchSchemaInput) => HostManagementSearchSchema.parse(search),
});

function RouteComponent() {
  const { getHostList, isLoading, pagination, additionalValue } = useHostList();
  return (
    <HostManagementTable
      data={getHostList}
      isLoading={isLoading}
      pagination={pagination}
      additionalValue={additionalValue}
    />
  );
}
